/**
 * Client for the Lejo API.
 *
 * One place that knows about tokens, refresh and error shapes, so no screen
 * has to. Screens call `api.get('/admin/documents')` and either receive typed
 * data or an ApiError they can render.
 *
 * Session storage: the access token lives in memory and the refresh token in
 * `localStorage`. Memory-only would log an operator out on every page load;
 * putting the access token in storage too would widen the blast radius of an
 * XSS. The access token is short-lived (15 minutes) and refresh tokens rotate
 * on use, so a stolen refresh token is usable once and then dead.
 */

const BASE = process.env.NEXT_PUBLIC_API_URL ?? 'https://api.lejoexpress.com';
const REFRESH_KEY = 'lejo.refresh';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the caller should be sent back to sign-in. */
  get isAuthFailure(): boolean {
    return this.status === 401;
  }
}

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export const session = {
  get accessToken() {
    return accessToken;
  },
  get refreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },
  set(tokens: { accessToken: string; refreshToken?: string }) {
    accessToken = tokens.accessToken;
    if (tokens.refreshToken && typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
      } catch {
        /* private browsing */
      }
    }
  },
  clear() {
    accessToken = null;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(REFRESH_KEY);
      } catch {
        /* ignore */
      }
    }
  },
};

/**
 * Exchanges the refresh token for a new pair.
 *
 * Concurrent callers share one in-flight refresh. Without that, several
 * requests hitting a just-expired token would each spend a refresh token, and
 * because rotation invalidates the previous one, all but the first would fail
 * and log the operator out mid-task.
 */
async function refresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  const token = session.refreshToken;
  if (!token) return false;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: token }),
      });
      if (!res.ok) {
        session.clear();
        return false;
      }
      const data = (await res.json()) as { accessToken: string; refreshToken: string };
      session.set(data);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Set false for the auth endpoints, which must not recurse into refresh. */
  retryOnAuthFailure?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, retryOnAuthFailure = true, signal } = options;

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;

    return fetch(`${BASE}${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  };

  let res: Response;
  try {
    res = await send();
  } catch (err) {
    // Network failure, CORS, or the API being down all land here. Screens can
    // distinguish this from a rejection by the status.
    throw new ApiError(0, 'network_error',
      'Could not reach the API. Check the connection and try again.', err);
  }

  if (res.status === 401 && retryOnAuthFailure && (await refresh())) {
    res = await send();
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? safeJson(text) : null;

  if (!res.ok) {
    const record = (payload ?? {}) as Record<string, unknown>;
    throw new ApiError(
      res.status,
      typeof record.error === 'string' ? record.error : 'request_failed',
      typeof record.message === 'string' ? record.message : describe(res.status),
      record,
    );
  }

  return payload as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function describe(status: number): string {
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'Not found.';
  if (status === 409) return 'That conflicts with the current state.';
  if (status === 429) return 'Too many attempts. Wait a moment and try again.';
  if (status >= 500) return 'The server had a problem. Try again shortly.';
  return 'The request could not be completed.';
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),

  /** Signs in. Does not retry on 401 -- a wrong code is an answer, not a stale session. */
  verify: (credential: string) =>
    request<AuthResponse>('/auth/verify', {
      method: 'POST',
      body: { credential },
      retryOnAuthFailure: false,
    }),

  /**
   * Signs in with an address and a password. Operators only.
   *
   * Exists because the phone route cannot be trusted to identify an operator
   * while no service is actually sending codes -- and because somebody has to
   * be able to get in when SMS fails.
   */
  signInWithPassword: (email: string, password: string) =>
    request<AuthResponse>('/auth/password', {
      method: 'POST',
      body: { email, password },
      retryOnAuthFailure: false,
    }),

  /** Restores a session on page load. Returns null when there is nothing to restore. */
  async restore(): Promise<Me | null> {
    if (!session.refreshToken) return null;
    if (!(await refresh())) return null;
    try {
      return await request<Me>('/me');
    } catch {
      session.clear();
      return null;
    }
  },

  /**
   * Fetches a stored file as an object URL.
   *
   * `/files/:id` requires an Authorization header, so a plain <img src> cannot
   * load it -- the browser sends no header and gets a 404. The caller must
   * revoke the returned URL when the image unmounts, or the blob leaks.
   */
  async fileObjectUrl(uploadId: string): Promise<string> {
    const headers: Record<string, string> = {};
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;

    let res = await fetch(`${BASE}/files/${uploadId}`, { headers });
    if (res.status === 401 && (await refresh())) {
      res = await fetch(`${BASE}/files/${uploadId}`, {
        headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
      });
    }
    if (!res.ok) {
      throw new ApiError(res.status, 'file_unavailable', describe(res.status));
    }
    return URL.createObjectURL(await res.blob());
  },

  baseUrl: BASE,
};

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  isNewUser: boolean;
  user: { id: string; phone: string; displayName: string | null; roles: string[] };
}

export interface Me {
  id: string;
  phone: string;
  displayName: string | null;
  email: string | null;
  regionCode: string | null;
  roles: string[];
  serverTime: string;
}
